import React, { useEffect, useMemo } from 'react';
import { Route, Switch } from 'react-router-dom';
import { AsideMenu } from '../components/AsideMenu';

import { useCommon } from '../../../contexts/CommonContext';
import { useTenant } from '../../../contexts/TenantContext';
import { useBillingConsumptionContext } from '../../../contexts/Billing/BillingConsumptionContext';
import { useBillingPlanUsageContext } from '../../../contexts/Billing/BillingPlanUsageContext';
import { useBillingMetricsContext } from '../../../contexts/Billing/BillingMetricsContext';

import { useContentLocalizer } from '../../../hooks/useContentLocalizer';

import AdvancedDailyEngagedUsersContext from '../../../contexts/Billing/AdvancedDailyEngagedUsersContext';
import { AdvancedDeuConsumptionContextProvider } from '../../../contexts/Billing/AdvancedDeuConsumptionContext';

import AdvancedDailyEngagedUsersSummary from './components/AdvancedDailyEngagedUsersSummary';

import BillingAdvancedDailyEngagedUserServiceApi from '../../../api/Billing/BillingAdvancedDailyEngagedUserApi';
import { action, origin, REPORT_ADVANCED_DEU_CONSUMPTION_OPEN, REPORT_ADVANCED_DEU_CONSUMPTION_CLOSED } from '../../../shared/SegmentEvents';
import { localization } from './i18n/localization';
import { billingRoutes, billingSubRoutes } from '../../../routes/BillingRoutes';
import MonthlyUsage from './components/Monthly/MonthlyUsage';
import DailyUsage from './components/Daily/DailyUsage';
import { usePageTracking } from '../hooks/usePageTracking';

const AdvancedDailyEngagedUsers: React.FC = () => {
  const { dateToSearch, setDateToSearch, periodOptions, currentMonth } =
    useBillingConsumptionContext();

  const { planStatus, getPlanStatus } = useBillingPlanUsageContext();
  const { isMetricAllowed } = useBillingMetricsContext();
  const { tenant } = useTenant();

  const {
    setHeaderContent,
    common: { loggedUser },
  } = useCommon();

  const PAGE_NAME = 'Consumo DEU Avançado';
  usePageTracking(PAGE_NAME, REPORT_ADVANCED_DEU_CONSUMPTION_OPEN, REPORT_ADVANCED_DEU_CONSUMPTION_CLOSED, tenant, loggedUser, true);

  const content = useContentLocalizer(localization);
  const billingAdvancedDailyEngagedUserApi = useMemo(() => new BillingAdvancedDailyEngagedUserServiceApi(), []);

  useEffect(() => {
    setHeaderContent({
      redirect: '/data-consumption',
      text: content.header,
    });

    return () => setHeaderContent({ redirect: null });
  }, [content.header, setHeaderContent]);

  const trackingProps = useMemo(
    () => ({
      date: dateToSearch,
      planStatus: planStatus.current,
      origin: origin.DEU,
      action: action.SELECT,
    }),
    [dateToSearch, planStatus.current],
  );

  const contextProps = useMemo(
    () => ({
      dateToSearch,
      setDateToSearch,
      periodOptions,
      currentMonth,
      tenant,
      loggedUser,
      content,
      billingAdvancedDailyEngagedUserApi,
      trackingProps,
      getPlanStatus,
      isMetricAllowed,
    }),
    [
      dateToSearch,
      setDateToSearch,
      periodOptions,
      currentMonth,
      tenant,
      loggedUser,
      content,
      billingAdvancedDailyEngagedUserApi,
      trackingProps,
      getPlanStatus,
      isMetricAllowed,
    ],
  );

  return (
    <AdvancedDailyEngagedUsersContext.Provider value={contextProps}>
      <AdvancedDeuConsumptionContextProvider tenant={tenant}>
        <AsideMenu>
          <Switch>
            <Route
              exact
              path={billingRoutes.advancedDeuConsumption}
              component={AdvancedDailyEngagedUsersSummary}
            />
            <Route
              exact
              path={billingSubRoutes.advancedDeuConsumptionMonthly}
              component={MonthlyUsage} />
            <Route 
              exact 
              path={billingSubRoutes.advancedDeuConsumptionDaily} 
              component={DailyUsage} />
          </Switch>
        </AsideMenu>
      </AdvancedDeuConsumptionContextProvider>
    </AdvancedDailyEngagedUsersContext.Provider>
  );
};

export default AdvancedDailyEngagedUsers;